import { Suspense, lazy, useEffect } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import "./components/styles/common.css";
import { ConfigProvider } from "antd";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import HideLayoutProvider from "./components/auth/SetHideLayoutOnAuth";
import LoginPage from "./components/auth/LoginPage";
import CallbackPage from "./components/auth/Callback";
import EmailVerificationPage from "./components/auth/EmailVerificationPage";
import SignupPage from "./components/auth/Signup";
import StudentLayout from "./components/layout/Layout";
import SessionExpired from "./errors/SessionExpired";
import ErrorPage from "./errors/CollegeLimitExceeded";
import NetworkErrorPage from "./NetworkErrorPage";
import { initPosthog } from "./posthog";
import { Stdconsole } from "./utils/setConsole";
import { store } from "./redux/store";

// Lazy load layout-heavy components
const Dashboard = lazy(() => import("./components/pages/Dashboard"));
const StudentProfile = lazy(() => import("./components/pages/StudentProfile"));

const routesConfig = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/signup",
    element: <SignupPage />
  },
  {
    path: "/callback",
    element: <CallbackPage />,
  },
  {
    path: "/verify-email",
    element: <EmailVerificationPage />
  },
  {
    path: "/error-mismatched-login",
    element: <ErrorPage messageText="This email is already registered with another provider. Please log in using the correct method." showLogin />
  },
  {
    path: "/error-page-not-found",
    element: <ErrorPage messageText=" Sorry, we couldn't find what you're looking for!" showGoBack />
  },
  {
    path: "/internal-server-error",
    element: <ErrorPage messageText="Oops! Something went wrong. Please try again later." showLogout />
  },
  {
    path: "/error-server-maintenance",
    element: <ErrorPage messageText="Server Under Maintenance, Please Try Again Later" showLogout />
  },
  {
    path: "/error-forbidden",
    element: <ErrorPage messageText="You do not have permission to access this resource." showLogout />
  },
  {
    path: "/network-error",
    element: <NetworkErrorPage />
  },
  {
    path: "/session-expired",
    element: <SessionExpired />
  },
  {
    path: "*",
    element: <Navigate to="/error-page-not-found" replace />,
  },
  {
    element: (
      <HideLayoutProvider>
        <StudentLayout />
      </HideLayoutProvider>
    ),
    children: [
      {
        path: "/",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/profile/:slug",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <StudentProfile />
            </Suspense>
          </ProtectedRoute>
        ),
      }
    ],
  },
];

const router = createBrowserRouter(routesConfig);

function App() {

  useEffect(() => {
    initPosthog();
    Stdconsole(store);
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: '#0056D2', fontFamily: 'Poppins, sans-serif' },
        components: { Menu: { colorPrimary: '#0056D2' } },
      }}
    >
      <Suspense fallback={null}>
        <RouterProvider router={router} />
      </Suspense>
    </ConfigProvider>
  );
}

export default App;